import { createSelector, Selector } from '@ngxs/store';
import { RouterState } from '@ngxs/router-plugin';

/* State */
import { AuthState } from '@core/store/auth/auth.state';
import { AppState } from './app.state';

/* Models */
import { Reference } from '@core/models/reference.model';
import { StaticData } from './app.model';
import { Node } from '@core/models/node.model';
import { TreeNode } from '@core/models/tree-node.model';
import { NodeType } from '@core/models/node-type.model';
import { DataType } from '@core/models/data-type.model';
import { User, UserAccount, UserSettings } from '@core/models/user.model';
import { Customer } from '@core/models/customer.model';
import { Country } from '@core/models/country.model';
import { NodeTypes } from '@core/enums';
import { AuthStateModel } from '../auth/auth.model';

/* Utils */
import { initalNodeTree } from '@core/constants';

/* Static data */
import countries from '@assets/static/countries.json';

export interface NodeMap {
	[id: number]: TreeNode;
}

export class AppSelectors {
	static getNodeType(nodeId: number | string) {
		return createSelector([AppSelectors.nodes], (nodes: Node[]) => {
			const node = nodes.find((n) => n.id === +nodeId);

			return node ? node.typeId : null;
		});
	}

	static getChildrenNodes(nodeId: number | string) {
		return createSelector([AppSelectors.nodes], (nodes: Node[]) => {
			const children = nodes.filter((n) => n.parentId === +nodeId);

			return children ?? null;
		});
	}

	static getParentNode(nodeId: number | string) {
		return createSelector([AppSelectors.nodes], (nodes: Node[]) => {
			const parentId = nodes.find((n) => n.id === +nodeId)?.parentId;
			const parent = parentId ? nodes.find((n) => n.id === parentId) : null;

			return parent ?? null;
		});
	}

	@Selector([AppState.staticData])
	static references(staticData: StaticData): Reference[] {
		return staticData.references.length ? staticData.references : null;
	}
	@Selector([AppState.staticData])
	static nodes(staticData: StaticData): Node[] {
		return staticData.nodes;
	}
	@Selector([AppState.staticData])
	static nodeTypes(staticData: StaticData): NodeType[] {
		return staticData.nodeTypes;
	}
	@Selector([AppState.staticData])
	static dataTypes(staticData: StaticData): DataType[] {
		return staticData.dataTypes;
	}
	@Selector([AppState.user])
	static isLoggedIn(user: User): boolean {
		return user !== null && user.id !== undefined;
	}
	@Selector([AppState.user])
	static username(user: User): string {
		return user.username !== undefined ? user.username : null;
	}

	@Selector([AppState.user, AppState.customer, AuthState])
	static userAccount(user: User, customer: Customer, authState: AuthStateModel): UserAccount {
		if (!user.id) {
			return null;
		}
		const { isAdmin, tfa } = authState;

		return { user, customer, isAdmin, tfa: tfa?.secret };
	}
	@Selector([AppState.user])
	static userSettings(user: User): UserSettings {
		return user.settings;
	}

	@Selector([AppSelectors.nodes, AppSelectors.nodeTypes])
	static nodeMap(nodes: Node[], nodeTypes: NodeType[]): NodeMap {
		const nodeMap = {};

		if (!nodes || !nodeTypes) {
			return nodeMap;
		}

		// convert nodes to tree node items
		nodes.forEach((node) => {
			const type = nodeTypes.find((n) => n.id === node.typeId);
			nodeMap[node.id] = new TreeNode(node, type);
		});

		// add children to parent nodes
		nodes.forEach((node) =>
		{
			if (node.parentId)
			{
				var parent = nodeMap[node.parentId];
				var treeNode = nodeMap[node.id];
				if (node.typeId === NodeTypes.Entrance)
				{
					parent.subAreas.push(treeNode);
					treeNode.subParent = parent;
				}
				else
				{
					parent.children.push(treeNode);
					treeNode.parent = parent;
				}
			}
		});

		for (var node in nodeMap)
		{
			nodeMap[node].children.sort((a, b) => a.name.localeCompare(b.name));
			nodeMap[node].subAreas.sort((a, b) => a.name.localeCompare(b.name));
		}
		return nodeMap;
	}

	@Selector([AppSelectors.nodeMap])
	static nodeTree(nodeMap: NodeMap): TreeNode[] {
		const nodeTree: TreeNode[] = [...initalNodeTree];
		if (!Object.keys(nodeMap).length) {
			return nodeTree;
		}

		// find root node and replace initial dashboard node
		const root = Object.values(nodeMap).find((node) => !node.parent);
		const index = nodeTree.findIndex((n) => n.name === 'buildings');
		nodeTree[index] = root;

		return nodeTree;
	}

	@Selector([RouterState.url, AppSelectors.nodeMap, AppSelectors.nodeTree])
	static activeNode(url: string, nodeMap: NodeMap, nodeTree: TreeNode[]): TreeNode {
		if (!url || !Object.keys(nodeMap).length) {
			return null;
		}

		if (url.includes('dashboard')) {
			const dashboardId = url.split('/')[2];

			return nodeMap[dashboardId];
		}

		const currentPath = '/' + url.split('/')[1];

		return nodeTree.find((node) => node.link === currentPath);
	}

	@Selector([RouterState.url])
	static currentPath(url: string): string {
		if (!url) {
			return '';
		}

		return url.split('?')[0].replace('/', '');
	}

	@Selector([AppSelectors.currentPath])
	static isLoginLayout(currentPath: string): boolean {
		return currentPath.includes('login') || currentPath.includes('reset-password');
	}

	@Selector([AppState.customer])
	static customerCountry(customer: Customer): Country {
		return countries.find((country) => country.id === customer.country);
	}
}
